if (!customElements.get('vg-product-intro')) {
  customElements.define(
    'vg-product-intro',
    class VgProductIntro extends HTMLElement {
      connectedCallback() {
        const sid = this.dataset.section;

        this._selectedQty = 1;
        this._qtyInput = this.querySelector(`#vg-qty-input-${sid}`);
        this._packCards = this.querySelectorAll('.vg-pdp-intro__pack');
        this._form = this.querySelector(`#vg-atc-form-${sid}`);
        this._priceStatus = this.querySelector('[data-price-status]');

        this._initPacks();
        this._initATC();
        this._initFloatingBar();
        this._syncHeaderHeight();
      }

      disconnectedCallback() {
        this._floatAbort?.abort();
      }

      // ── Mobile purchase bar ───────────────────────────────────────────
      // The bar is sticky, but sticky can't rise above this column, which sits under the gallery on mobile.
      // While the column is still below the bar's slot, pin the bar to the viewport instead. Both modes put
      // the bar at the viewport bottom around the switch, so it is invisible; switching one bar-height
      // early leaves slack for a frame of scroll lag (needs ≥ one bar-height of content above the bar).
      // State is re-read from layout on every scroll/resize, so a jump (anchor link, restore) can't strand it.

      _initFloatingBar() {
        const bar =
          this.querySelector('.vg-pdp-intro__purchase-wrap') ||
          this.querySelector('.vg-pdp-intro__atc-btn')?.form;
        if (!bar) return;
        const mobile = window.matchMedia('(max-width: 1023px)');
        let frame = 0;
        const update = () => {
          frame = 0;
          const slot = window.innerHeight - 2 * bar.offsetHeight;
          bar.classList.toggle(
            'is-floating',
            mobile.matches && this.getBoundingClientRect().top > slot,
          );
        };
        const schedule = () => {
          if (!frame) frame = requestAnimationFrame(update);
        };
        this._floatAbort?.abort();
        this._floatAbort = new AbortController();
        const opts = {passive: true, signal: this._floatAbort.signal};
        window.addEventListener('scroll', schedule, opts);
        window.addEventListener('resize', schedule, opts);
        window.addEventListener('load', schedule, {...opts, once: true});
        update();
      }

      // ── Pack selection ────────────────────────────────────────────────

      _initPacks() {
        const first = this._packCards[0];
        if (first) this._selectPack(first);

        this._packCards.forEach((card) => {
          card.addEventListener('click', () => this._selectPack(card, true));
        });
      }

      _selectPack(card, announce = false) {
        this._packCards.forEach((c) => {
          c.classList.remove('vg-pdp-intro__pack--active');
          c.setAttribute('aria-pressed', 'false');
        });
        card.classList.add('vg-pdp-intro__pack--active');
        card.setAttribute('aria-pressed', 'true');

        this._selectedQty = parseInt(card.dataset.qty, 10) || 1;
        if (this._qtyInput) this._qtyInput.value = this._selectedQty;
        this._updatePrices(card);
        if (announce) this._announcePrices(card);
      }

      _announcePrices(card) {
        if (!this._priceStatus) return;
        const parts = [
          card.querySelector('.vg-pdp-intro__pack-label')?.textContent.trim(),
        ];
        this.querySelectorAll('.vg-pdp-intro__tab-prices').forEach((el) =>
          parts.push(el.textContent.replace(/\s+/g, ' ').trim()),
        );
        this._announce(this._priceStatus, parts.filter(Boolean).join(', '));
      }

      _announce(el, message) {
        if (!el) return;
        el.textContent = '';
        setTimeout(() => {
          el.textContent = message;
        }, 100);
      }

      // ── Price display ─────────────────────────────────────────────────

      _updatePrices(packCard) {
        const unitPrice = parseInt(packCard.dataset.unitPrice, 10);
        const subPrice = parseInt(packCard.dataset.subPrice, 10);
        const fmt = (cents) =>
          FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);

        // Subscribe tab prices
        const priceSubEl = this.querySelector('.vg-pdp-intro__price-sub');
        const priceCompareEl = this.querySelector(
          '.vg-pdp-intro__price-compare',
        );
        if (priceSubEl) {
          priceSubEl.textContent = fmt(subPrice);
          priceSubEl.dataset.subPrice = subPrice;
        }
        if (priceCompareEl) {
          priceCompareEl.textContent = fmt(unitPrice);
          priceCompareEl.dataset.unitPrice = unitPrice;
        }

        // Subscribe savings per delivery
        const savingsEl = this.querySelector('[data-sub-savings]');
        if (savingsEl) {
          const savings = Math.max(0, (unitPrice - subPrice) * this._selectedQty);
          savingsEl.textContent = fmt(savings);
          savingsEl.closest('.vg-pdp-intro__tab-header').hidden = savings === 0;
        }

        // One-time tab price
        const priceOtEl = this.querySelector('.vg-pdp-intro__price-ot');
        if (priceOtEl) {
          priceOtEl.textContent = fmt(unitPrice);
          priceOtEl.dataset.unitPrice = unitPrice;
        }
      }

      // ── Header height sync ────────────────────────────────────────────

      _syncHeaderHeight() {
        const header = document.querySelector('header');
        if (!header) return;
        const set = () => {
          const h = header.offsetHeight;
          if (h)
            document.documentElement.style.setProperty(
              '--header-height',
              `${h}px`,
            );
        };
        set();
        if (window.ResizeObserver) new ResizeObserver(set).observe(header);
      }

      // ── Add to Cart ───────────────────────────────────────────────────

      _initATC() {
        if (!this._form) return;
        this._form.addEventListener('submit', (e) => {
          e.preventDefault();
          this._submitATC(e.submitter);
        });
      }

      async _submitATC(submitter) {
        // form.elements includes the purchase buttons linked via form="…"
        const buttons = [...this._form.elements].filter(
          (el) => el.type === 'submit',
        );
        const btn = submitter || buttons[0];
        const statusEl = this._form.querySelector('[data-atc-status]');
        const errorEl = this._form.querySelector('[data-atc-error]');
        const hadFocus = btn && btn === document.activeElement;
        if (errorEl) errorEl.textContent = '';
        btn?.setAttribute('aria-busy', 'true');
        buttons.forEach((b) => (b.disabled = true));

        try {
          const sections = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', {
              bubbles: true,
              detail: {sections},
            }),
          );

          const variantId = this._form.querySelector('input[name="id"]').value;
          const qty = this._selectedQty ?? 1;
          const sellingPlan = btn?.value || '';

          const body = {
            id: variantId,
            quantity: qty,
            sections,
            sections_url: window.location.pathname,
          };
          if (sellingPlan) body.selling_plan = sellingPlan;

          const res = await fetch(FoxTheme.routes.cart_add_url, {
            ...FoxTheme.utils.fetchConfig('javascript'),
            body: JSON.stringify(body),
          });
          const data = await res.json();

          if (data.status) {
            console.error(
              '[vg-product-intro] Cart add error:',
              data.description,
            );
            this._announce(
              errorEl,
              data.description || data.message || this._form.dataset.msgError,
            );
            return;
          }

          const cartJson = await fetch(
            FoxTheme.routes.cart_url,
            FoxTheme.utils.fetchConfig(),
          ).then((r) => r.json());
          if (data.sections) cartJson.sections = data.sections;
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            cart: cartJson,
          });
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {detail: {open: true}}),
          );
          this._announce(statusEl, this._form.dataset.msgAdded);
        } catch (err) {
          console.error('[vg-product-intro] ATC error:', err);
          this._announce(errorEl, this._form.dataset.msgError);
        } finally {
          buttons.forEach((b) => (b.disabled = false));
          if (btn) {
            btn.removeAttribute('aria-busy');
            const active = document.activeElement;
            if (hadFocus && (!active || active === document.body)) btn.focus();
          }
        }
      }
    },
  );
}
